import { useCallback, useState, useRef, useEffect } from 'react';
import { isEqual } from 'lodash';

interface UseFormChangeTrackerProps<T> {
  currentData: T;
  hasNewNote: boolean;
  isDataLoaded?: boolean;
  isCreateMode?: boolean;
}

export const useFormChangeTracker = <T>({
  currentData,
  hasNewNote,
  isDataLoaded = true,
  isCreateMode = false,
}: UseFormChangeTrackerProps<T>) => {
  const [requiresNote, setRequiresNote] = useState(false);
  const [lastCheckedChanges, setLastCheckedChanges] = useState<string[]>([]);
  const baselineDataRef = useRef<T | null>(null);

  const [userNotified, setUserNotified] = useState(false);

  useEffect(() => {
    if (isCreateMode) {
      return;
    }

    if (isDataLoaded && currentData && !baselineDataRef.current) {
      baselineDataRef.current = JSON.parse(JSON.stringify(currentData));
    }
  }, [currentData, isDataLoaded, isCreateMode]);

  // Only check for changes when explicitly called (on save attempt)
  const checkForChanges = useCallback((): { hasChanges: boolean; changes: string[]; canSave: boolean } => {
    if (isCreateMode) {
      const needsNote = !hasNewNote;
      setRequiresNote(needsNote);
      setLastCheckedChanges(['created form']);

      return {
        hasChanges: true, // Always true in create mode
        changes: ['created form'],
        canSave: !needsNote,
      };
    }

    if (!baselineDataRef.current || !isDataLoaded) {
      return { hasChanges: false, changes: [], canSave: true };
    }

    const changes: string[] = [];

    if (
      typeof currentData === 'object' &&
      typeof baselineDataRef.current === 'object' &&
      currentData &&
      baselineDataRef.current
    ) {
      Object.keys(currentData as Record<string, unknown>).forEach(key => {
        const currentValue = (currentData as Record<string, unknown>)[key];
        const baselineValue = (baselineDataRef.current as Record<string, unknown>)[key];
        if (!isEqual(currentValue, baselineValue)) {
          changes.push(key);
        }
      });
    }

    const hasChanges = changes.length > 0;
    const needsNote = hasChanges && !hasNewNote;

    setRequiresNote(needsNote);
    setLastCheckedChanges(changes);

    return {
      hasChanges,
      changes,
      canSave: !needsNote,
    };
  }, [currentData, hasNewNote, isDataLoaded, isCreateMode]);

  const triggerNoteRequirement = useCallback(() => {
    setUserNotified(true);
  }, []);

  const getChangesSummary = useCallback((): string[] => {
    return lastCheckedChanges;
  }, [lastCheckedChanges]);

  useEffect(() => {
    if (hasNewNote && requiresNote) {
      setRequiresNote(false);
      if (userNotified) {
        setUserNotified(false);
      }
    }
  }, [hasNewNote, requiresNote, userNotified]);

  const resetBaseline = useCallback(() => {
    if (currentData) {
      baselineDataRef.current = JSON.parse(JSON.stringify(currentData));
      setRequiresNote(false);
      setLastCheckedChanges([]);
      setUserNotified(false);
    }
  }, [currentData]);

  // Lightweight check for real-time UI feedback (no deep comparison)
  const hasLikelyChanges = useCallback((): boolean => {
    if (isCreateMode) return true; // Always true in create mode

    if (!baselineDataRef.current || !isDataLoaded) return false;
    return lastCheckedChanges.length > 0;
  }, [lastCheckedChanges, isDataLoaded, isCreateMode]);

  return {
    checkForChanges, // Main function to call when save is attempted
    resetBaseline, // Function to reset the baseline (useful after save)
    hasLikelyChanges, // Lightweight check for real-time UI feedback
    requiresNote,
    userNotified,
    triggerNoteRequirement,
    getChangesSummary,
  };
};
